var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import SideBarTemplate from '@front/templates/SideBarTemplate.html';
import { IconButtonType } from '@components/webdesktop/elements/IconButton/IconButtonTypes.interface';
import { SvgIconType } from '@components/webdesktop/elements/SvgIcon/SvgIconType';
import { ExtLinkClientTab } from '@shared/commons/cache/ExternalLink/ExtLinkClientTab';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { PanelDisplayMode } from '@shared/commons/CustomerAccess/PanelDisplayMode';
import { DataCache } from '@shared/commons/DataCache';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { SideBarButtonsHelper } from '@shared/commons/SideBar/SideBarButtonsHelper';
import { Resources } from '@shared/localizations/Resources';
import { externalLinkManager } from '@shared/utils/Managers/ExternalLinkManager';
import { DropDownSideBar } from './DropDownSideBar';
import { PanelNames, ScreensNames } from '../../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { ContainerControl } from '../ContainerControl';
export class SideBar extends ContainerControl {
    constructor() {
        super(...arguments);
        this.activeMainMenuBtn = null;
        this.OnConnectedStatusChanged = (connectedStatus) => {
            void super.set({
                connectedStatus,
                localizedSettingsDisabledTT: Resources.getResource('ribbon.enviroment.settings.disabled.tt')
            });
        };
        this.ReInitialVisible = () => {
            this.initialVisibleAlertsPanel();
            this.initialVisibleNotificationsPanel();
            this.initialVisibleVideoWidget();
            void super.set({ showQRCodeButton: externalLinkManager.isAllowedMobileApps() });
        };
        this.onQRCodeButtonClick = (event) => {
            if (this.get('qrCodeButtonSelected')) {
                const qrCodeScreen = MainWindowManager.GetScreen(ScreensNames.QRCodeScreen);
                qrCodeScreen.close();
                return;
            }
            const screen = MainWindowManager.Factory.addScreen(ScreensNames.QRCodeScreen);
            const items = externalLinkManager
                .getLinksFilteredByClientTab(ExtLinkClientTab.MOBILE_APPS)
                .map((link) => {
                var _a;
                return ({
                    id: link.id.toString(),
                    title: link.getLocalizedToolName(),
                    description: (_a = link.getLocalizedTabNameOrNull()) !== null && _a !== void 0 ? _a : link.description,
                    url: link.urlStr
                });
            });
            items.sort((a, b) => a.title.localeCompare(b.title));
            const buttonRect = event.currentTarget.getBoundingClientRect();
            const dockMainContainer = document.getElementById('dockMainContainer');
            const workspaceRect = dockMainContainer.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            void screen.set({
                left: workspaceRect.left,
                bottom: viewportHeight - buttonRect.bottom,
                triggerElement: event.currentTarget,
                qrCodeScreenItems: items,
                selectedItemId: BaseSettings.mobileAppQRCodeTabId,
                onUpdateLastSelectedItemCallback: (lastSelectedItem) => {
                    BaseSettings.mobileAppQRCodeTabId = lastSelectedItem.id;
                }
            });
            screen.on('windowCloseCallback', () => {
                void this.set({ qrCodeButtonSelected: false });
            });
            this.set('qrCodeButtonSelected', true);
        };
    }
    getType() {
        return 'SideBar';
    }
    oncomplete() {
        super.oncomplete();
        DataCache.OnReintialize.Subscribe(this.ReInitialVisible);
        this.subscribeToBtnsEvents();
        DropDownSideBar.ClosePanel.Subscribe(this.closeSidePanel, this);
        SideBarButtonsHelper.subscribeOnConnectedStatusChanged(this.OnConnectedStatusChanged);
        void super.set({ isHelpVisible: !Resources.isHidden('ribbon.enviroment.help') });
        this.localize();
    }
    closeSidePanel() {
        void super.set({
            visibleSidePanel: false,
            activeSidePanel: null
        });
    }
    subscribeToBtnsEvents() {
        super.on('alertClick', () => {
            const panelType = PanelNames.AlertPanel;
            void this.changeVisibleStatePanel(panelType);
        });
        super.on('notificationsClick', () => {
            const panelType = PanelNames.NotificationsPanel;
            void this.changeVisibleStatePanel(panelType);
        });
        super.on('videoWidgetBtnClick', () => {
            const panelType = PanelNames.VideoWidget;
            void this.changeVisibleStatePanel(panelType);
        });
        super.on('helpClick', (btnContext) => {
            window.open(Resources.getResource('helpPage.link'));
            const btn = btnContext.component;
            btn.lostFocus();
        });
        super.on('settingsClick', () => {
            if (SideBarButtonsHelper.connectedStatus) {
                MainWindowManager.MainWindow.showGeneralSetting();
            } // #111602
        });
        super.on('disconnectClick', () => {
            MainWindowManager.MainWindow.disconnectClick();
        });
        this.on('collapseButtonClick', this.onCollapseButtonClick);
    }
    changeVisibleStatePanel(type) {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const activeSidePanel = _super.get.call(this, 'activeSidePanel');
            if (activeSidePanel === type) {
                this.closeSidePanel();
                return;
            }
            else {
                yield _super.set.call(this, 'visibleSidePanel', true);
                this.registerAllControl();
            }
            void _super.set.call(this, {
                activeSidePanel: type
            });
        });
    }
    initialVisibleAlertsPanel() {
        const alertsVisible = DataCache.AlertManager.AlertsVisible;
        void super.set('alertsBtnVisible', alertsVisible);
    }
    initialVisibleNotificationsPanel() {
        const notificationsVisible = CustomerAccess.GetDisplayMode(PanelNames.NotificationsPanel) === PanelDisplayMode.Allowed &&
            !Resources.isHidden('panel.Notifications');
        void super.set('notificationsBtnVisible', notificationsVisible);
    }
    initialVisibleVideoWidget() {
        const videoWidgetVisible = CustomerAccess.GetDisplayMode(PanelNames.VideoWidget) === PanelDisplayMode.Allowed &&
            !Resources.isHidden('panel.VideoWidget');
        void super.set('videoWidgetBtnVisible', videoWidgetVisible);
    }
    onMouseDown(event) { }
    lostFocus() {
        var _a;
        if (this.isLostFocusForbidden() || this.isSettingsOpen()) {
            if (this.customLostFocus()) {
                this.AfterLostFocusWaiting = this;
            } // return focus back to sideBar. SideBarPanel is opened;
            return;
        }
        (_a = this.get('SideBarPanelRef')) === null || _a === void 0 ? void 0 : _a.lostFocus();
        super.lostFocus();
        this.closeSidePanel();
    }
    isLostFocusForbidden() {
        var _a;
        return (_a = this.get('SideBarPanelRef')) === null || _a === void 0 ? void 0 : _a.isLostFocusForbidden();
    }
    customLostFocus() {
        var _a;
        return (_a = this.get('SideBarPanelRef')) === null || _a === void 0 ? void 0 : _a.customLostFocus(this);
    }
    isSettingsOpen() {
        var _a;
        return (_a = this.get('SideBarPanelRef')) === null || _a === void 0 ? void 0 : _a.isSettingsOpen();
    }
    localize() {
        void this.set({
            helpTooltip: Resources.getResource('ribbon.enviroment.help'),
            settingsTooltip: Resources.getResource('ribbon.enviroment.settings'),
            logoutTooltip: Resources.getResource('ribbon.enviroment.logout'),
            qrCodeButtonTooltip: Resources.getResource('ribbon.enviroment.showQRCode'),
            showSideBarTooltip: Resources.getResource('sidebar.showSidebar.tooltip'),
            hideSideBarTooltip: Resources.getResource('sidebar.hideSidebar.tooltip')
        });
    }
    onCollapseButtonClick() {
        const toggleCollapse = this.get('toggleCollapse');
        toggleCollapse();
    }
}
ContainerControl.extendWith(SideBar, {
    template: SideBarTemplate,
    data: function () {
        return {
            PanelNames,
            alertsBtnVisible: true,
            videoWidgetBtnVisible: true,
            notificationsBtnVisible: true,
            localkeyVideoWidget: '',
            visibleSidePanel: false,
            activeSidePanel: null,
            isHelpVisible: true,
            connectedStatus: false,
            localizedSettingsDisabledTT: '',
            helpTooltip: '',
            settingsTooltip: '',
            logoutTooltip: '',
            showSideBarTooltip: '',
            hideSideBarTooltip: '',
            toggleCollapse: null,
            isCollapsed: false,
            isGeneralSettingsOpened: false,
            showQRCodeButton: false,
            qrCodeButtonTooltip: '',
            qrCodeButtonIcon: SvgIconType.QRSidebar,
            qrCodeButtonMode: IconButtonType.Action,
            qrCodeButtonSelected: false
        };
    }
});
